import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchFromApi } from 'api/apiClient';
import { useUI } from 'contexts/UiContext';
import { SortOrder } from 'hooks/useTableControls';
import { PaginationQueryParams } from 'types/queryTypes';
import { BulkEditUomsPayload, UnitOfMeasure } from 'types/unitOfMeasureTypes';

const UMOS_QUERY_KEY = 'uoms';
const REACT_APP_DARWIN_URL = process.env.REACT_APP_DARWIN_URL;

/**
 * Fetch uoms.
 */
export const useGetUnitsOfMeasure = ({
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'id', order: 'ASC' },
}: Partial<PaginationQueryParams> & {
  sortOrder: SortOrder;
}) => {
  const { selectedCompany } = useUI();
  return useQuery({
    queryKey: [UMOS_QUERY_KEY, limit, page, query, sortOrder],
    queryFn: () =>
      fetchFromApi<UnitOfMeasure[]>(
        `${REACT_APP_DARWIN_URL}/unit-of-measure/${selectedCompany}/units?limit=${limit}&page=${page}&filter=${query}&sortBy=${sortOrder.sortBy}&order=${sortOrder.order}`
      ),
    refetchOnWindowFocus: false,
  });
};

/**
 * Bulk save uoms.
 */
export const useEditUoms = () => {
  const queryClient = useQueryClient();
  const { selectedCompany } = useUI();

  return useMutation({
    mutationFn: ({ uomsData }: { uomsData: BulkEditUomsPayload }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/unit-of-measure/${selectedCompany}/units/bulk-update`,
        {
          method: 'PATCH',
          body: JSON.stringify(uomsData),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [UMOS_QUERY_KEY] });
    },
  });
};
